import {compact, noop} from 'lodash';
import {hslToColorString} from 'polished';
import {HslaColor} from 'polished/lib/types/color';
import {useCallback, useLayoutEffect, useRef} from 'react';
import {Key} from 'ts-key-enum';

import {
  forEachElementInTree,
  getWindowForElement,
  isHtmlElement,
} from '../../../helpers/src/browser/domHelpers';
import {collectEnumValues} from '../../../helpers/src/typescript/enumHelpers';
import {isExpandedDarkTheme} from '../theme/themeTypeHelpers';
import {useUserTheme} from '../theme/userThemeProvider';
import {
  computeInvertedBorderColor,
  computeInvertedColor,
  CssPropertiesEnum,
  parseToHslaMemoized,
} from './darkModeHelpers';

/*
 * Constants.
 */

// Classes and IDs are prefixed with this string.
const IDENTIFIER_PREFIX = 'front-dark-theme';

// This regex can be used to remove the identifiers from strings.
export const IDENTIFIER_REGEX = /front-dark-theme-\d+/g;

// This regex parses the color value out of CSS var statements.
// e.g. var(--variableName, #000);
const CSS_VAR_REGEX = /var\(--.+?,(.+)\)/;

/*
 * Types.
 */

interface InvertedColorInfo {
  invertedStyle: string;
  className: string;
}

interface ColorInfo {
  [key: string]: InvertedColorInfo;
}

/*
 * Helpers.
 */

let nextId = 1;
export function buildUniqueIdentifier() {
  return `${IDENTIFIER_PREFIX}-${nextId++}`;
}

// Used to remove identifiers added by this module from HTML strings.
export function removeDarkModeClassNames(string: string) {
  return string.replace(IDENTIFIER_REGEX, '');
}

// Generates the CSS which is inserted into the <style> tag.
function createStyleString(colorInfo: ColorInfo) {
  return Object.values(colorInfo)
    .map(({invertedStyle, className}) => `.${className}{${invertedStyle}!important}`)
    .join(' ');
}

export function parseFallbackColorFromCssFunction(color: string) {
  const match = color.match(CSS_VAR_REGEX);
  if (match && match[1]) {
    return match[1].trim();
  }

  return color;
}

export function findHslaColor(originalColor: string) {
  try {
    const color = parseFallbackColorFromCssFunction(originalColor);
    return parseToHslaMemoized(color);
  } catch (e) {
    // Handles transparent and malformed color strings which
    // should be left as-is.
    return null;
  }
}

// Adds classNamesToAdd to the node. Removes dark mode classNames which
// aren't in the array.
function resolveClassList(node: HTMLElement, classNamesToAdd: string[]) {
  for (const className of Array.from(node.classList)) {
    if (className.startsWith(IDENTIFIER_PREFIX) && !classNamesToAdd.includes(className)) {
      node.classList.remove(className);
    }
  }

  for (const className of classNamesToAdd) {
    node.classList.add(className);
  }
}

export function computeInvertedEditorColor(hsla: HslaColor) {
  // If the user is trying to use very light colored text (e.g. white,)
  // that's probably wrong. In this case we don't try to clamp the
  // lightness, so it's obvious to the user that they're making a mistake.
  if (hsla.lightness > 0.8) {
    return hslToColorString({
      ...hsla,
      lightness: 1 - hsla.lightness,
    });
  }

  // Otherwise, fallback onto the message color inversion logic.
  return computeInvertedColor(hsla);
}

function computeInvertedEditorBackgroundColor(hsla: HslaColor) {
  // We don't follow the same background inversion logic as the message
  // viewer, because we want it to be obvious a background color is being
  // set. Lightness is reduced because text is hard to see on most colors
  // with simple inversion.
  return hslToColorString({
    ...hsla,
    lightness: (1 - hsla.lightness) * 0.4,
  });
}

type ColorInverter = (hslColor: Readonly<HslaColor>) => string;

const colorInverterFunctions: Record<CssPropertiesEnum, ColorInverter> = {
  [CssPropertiesEnum.BACKGROUND_COLOR]: computeInvertedEditorBackgroundColor,
  [CssPropertiesEnum.COLOR]: computeInvertedEditorColor,
  [CssPropertiesEnum.BORDER_BOTTOM_COLOR]: computeInvertedBorderColor,
  [CssPropertiesEnum.BORDER_LEFT_COLOR]: computeInvertedBorderColor,
  [CssPropertiesEnum.BORDER_RIGHT_COLOR]: computeInvertedBorderColor,
  [CssPropertiesEnum.BORDER_TOP_COLOR]: computeInvertedBorderColor,
};

/*
 * Class.
 */

// Maintains state related to the dark mode functionality. This logic
// all happens outside of React.
class EditorDarkModeManager {
  // Maintains a map between color styles and generated classNames
  // to apply to DOM nodes.
  private colorInfo: ColorInfo = {};

  // Used to determine which nodes to color invert.
  private mutationObserver: MutationObserver | null = null;

  // Whether the mutation observer is connected.
  private isObserving = false;

  // A reference to the style node which contains the CSS style string.
  private styleNode: HTMLStyleElement | null = null;

  // The top level node which is being color inverted.
  private root: HTMLElement;

  // The document element for the node
  private document: Document;

  constructor(root: HTMLElement) {
    this.root = root;
    this.document = root.ownerDocument;
  }

  constructMutationObserver(root: HTMLElement) {
    const window = getWindowForElement(root);
    if (!window) {
      return null;
    }

    return new window.MutationObserver(this.handleMutation.bind(this));
  }

  // Does an initial color inversion, and watches for changes.
  start() {
    if (this.isObserving) {
      return;
    }

    this.isObserving = true;
    if (!this.mutationObserver) {
      this.mutationObserver = this.constructMutationObserver(this.root);
    }

    // This should only happen if window is undefined for some reason (e.g. jsdom.)
    // In that case, exit early.
    if (!this.mutationObserver) {
      return;
    }

    forEachElementInTree(this.root, (node) => this.updateNodeStyles(node));
    this.setStyleNodeCssString();
    this.root.addEventListener('keydown', this.handleKeydown, true);
    this.mutationObserver.observe(this.root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style'],
      // Observe changes to text nodes. Since we remove styles in the
      // keydown listener, the MutationObserver callback needs to be
      // run on any edit to add the removed styles back.
      characterData: true,
    });
  }

  disconnect() {
    this.isObserving = false;
    this.mutationObserver?.disconnect();
    this.root.removeEventListener('keydown', this.handleKeydown, true);
  }

  teardown() {
    this.disconnect();
    this.removeStyleNode();
  }

  handleKeydown = (event: KeyboardEvent) => {
    if (event.key !== Key.Backspace && event.key !== Key.Enter) {
      return;
    }

    // Styles are temporarily removed so the browser can correctly calculate
    // styles when adding and removing list item elements. The styles are
    // added back as a part of the MutationObserver callback.
    this.removeStyleNode();
  };

  handleMutation(mutations: MutationRecord[]) {
    for (const mutation of mutations) {
      switch (mutation.type) {
        case 'childList':
          this.handleChildListMutation(mutation);
          break;
        case 'attributes':
          this.handleAttributesMutation(mutation);
          break;
        default:
        // noop
      }
    }
    this.setStyleNodeCssString();
    // Prevent our own mutations from retriggering the observer.
    this.mutationObserver?.takeRecords();
  }

  handleChildListMutation(mutation: MutationRecord) {
    if (mutation.type !== 'childList') {
      return;
    }

    for (const addedNode of Array.from(mutation.addedNodes)) {
      if (isHtmlElement(addedNode)) {
        forEachElementInTree(addedNode, (node) => this.updateNodeStyles(node));
      }
    }
  }

  handleAttributesMutation(mutation: MutationRecord) {
    if (mutation.type !== 'attributes') {
      return;
    }

    if (isHtmlElement(mutation.target)) {
      this.updateNodeStyles(mutation.target);
    }
  }

  // This method generates classNames for style strings. For example, it might map
  // 'color:#000': 'front-dark-123456-1'
  // If a classname has already been generated for a color (which is fairly common, since
  // users usually stick to a few colors,) it returns the already generated className.
  computeClassNameForStyle(
    cssProperty: CssPropertiesEnum,
    color: HslaColor,
    colorInversionFn: (hslaColor: HslaColor) => string,
  ) {
    const key = `${cssProperty}:${hslToColorString(color)}`;
    if (!this.colorInfo[key]) {
      this.colorInfo[key] = {
        invertedStyle: `${cssProperty}:${colorInversionFn(color)}`,
        className: buildUniqueIdentifier(),
      };
    }

    return this.colorInfo[key].className;
  }

  processColor(property: CssPropertiesEnum, color: string) {
    const hsla = findHslaColor(color);

    if (!hsla) {
      return '';
    }

    return this.computeClassNameForStyle(property, hsla, colorInverterFunctions[property]);
  }

  updateNodeStyles(node: HTMLElement) {
    const classNamesToAdd = compact(
      collectEnumValues(CssPropertiesEnum).map((property) =>
        this.processColor(property, node.style.getPropertyValue(property)),
      ),
    );

    resolveClassList(node, classNamesToAdd);
  }

  insertStyleNode() {
    const node = this.document.createElement('style');
    this.document.head.appendChild(node);
    this.styleNode = node;
    return node;
  }

  findStyleNode() {
    return this.styleNode;
  }

  removeStyleNode() {
    this.styleNode?.remove();
    this.styleNode = null;
  }

  setStyleNodeCssString() {
    const styleString = createStyleString(this.colorInfo);
    if (!styleString) {
      this.removeStyleNode();
      return;
    }

    const node = this.findStyleNode() || this.insertStyleNode();
    node.textContent = styleString;
  }
}

/*
 * Hook.
 */

// Primary export. Returns a ref callback. The node passed into the
// callback will be converted to dark mode. This only inverts colors
// set on a node's inline styles. Make sure you set a default text
// color as a part of your component's normal CSS.
//
// const StyledDiv = `
//   color: ${p => p.theme.composerText};
// `;
//
// const SampleComponent = () => {
//   const editorDarkModeRef = useEditorDarkMode();
//   return <StyledDiv ref={editorDarkModeRef}></StyledDiv>
// }
//
// Class names added by this module can be removed with the exported
// removeDarkModeClassNames() function.
export const useEditorDarkMode = () => {
  const userTheme = useUserTheme();
  const elementRef = useRef<HTMLElement | null>();
  const managerRef = useRef<EditorDarkModeManager | null>();
  const isEnabled = isExpandedDarkTheme(userTheme);

  useLayoutEffect(() => {
    const manager = managerRef.current;
    if (!manager) {
      return noop;
    }

    if (isEnabled) {
      manager.start();
    } else {
      manager.teardown();
    }

    return () => {
      manager.teardown();
    };
  }, [isEnabled]);

  return useCallback(
    (element: HTMLElement | null) => {
      if (elementRef.current) {
        managerRef.current?.teardown();
        managerRef.current = null;
      }

      elementRef.current = element;
      if (!element) {
        return;
      }

      const manager = new EditorDarkModeManager(element);
      managerRef.current = manager;

      if (isEnabled) {
        manager.start();
      }
    },
    [isEnabled],
  );
};
